import React from 'react';
import { closestCenter, DndContext, DragOverlay } from '@dnd-kit/core';
import { rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { flex } from '@fabric-ui/theme';
import { Box } from '@mui/material';
import { END_DROP_ID } from './reorderFilters';
import { useFilterDnd } from './useFilterDnd';

/**
 * Invisible, flex-growing drop target rendered after the last chip. Gives the
 * user a forgiving "drop anywhere past the last filter" target so appending is
 * no longer "land exactly on the last chip".
 */
const EndDropSlot = () => {
  const { setNodeRef } = useSortable({ id: END_DROP_ID });
  return <Box ref={setNodeRef} data-testid="av-filters-end-drop-slot" sx={{ flex: 1, alignSelf: 'stretch', minWidth: 16 }} />;
};

interface FilterDragOverlayProps {
  activeId: string | null;
  renderItem: (id: string) => React.ReactNode;
}

/**
 * Portal that renders a clone of the dragged chip following the pointer. The
 * original chip stays in place as a placeholder so the user sees exactly where
 * the drop will land while the floating clone tracks the cursor.
 */
const FilterDragOverlay = ({ activeId, renderItem }: FilterDragOverlayProps) => (
  <DragOverlay dropAnimation={null}>
    {activeId ? <Box sx={{ position: 'relative', cursor: 'grabbing' }}>{renderItem(activeId)}</Box> : null}
  </DragOverlay>
);

interface FilterDndContextProps {
  order: string[];
  isDraggable: boolean;
  disabled: boolean;
  onReorder: (newOrder: string[]) => void;
  renderItem: (id: string) => React.ReactNode;
  children: React.ReactNode;
}

/**
 * Composition root for the filter chip DnD. Wires the DndContext, the
 * SortableContext (with the end-drop sentinel registered), the wrapping flex
 * container, and the floating DragOverlay together. Callers supply the order,
 * the per-item renderer (used by the overlay), and the children (the actual
 * SortableFilter chips).
 */
export const FilterDndContext = ({ order, isDraggable, disabled, onReorder, renderItem, children }: FilterDndContextProps) => {
  const { sensors, activeId, handleDragStart, handleDragEnd, handleDragCancel } = useFilterDnd({
    order,
    isDraggable,
    disabled,
    onReorder,
  });

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}>
      <SortableContext items={[...order, END_DROP_ID]} strategy={rectSortingStrategy}>
        <Box sx={{ ...flex.itemsCenter, flexWrap: 'wrap', gap: '5px' }}>
          {children}
          <EndDropSlot />
        </Box>
      </SortableContext>
      <FilterDragOverlay activeId={activeId} renderItem={renderItem} />
    </DndContext>
  );
};
